import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface DecayCurveProps {
  /** Start price/amount (best) */
  startAmount: number;
  /** End price/amount (worst) */
  endAmount: number;
  /** Decay duration in seconds */
  decayDuration: number;
  /** Seconds since order creation (for the "now" marker) */
  elapsedSeconds?: number;
  /** Fill price if filled */
  fillPrice?: number;
  /** Height in px */
  height?: number;
  /** Title label above the chart */
  title?: string;
  /** Whether to show a compact version */
  compact?: boolean;
  /** Output token symbol for Y-axis label */
  outputSymbol?: string;
}

export function DecayCurve({
  startAmount,
  endAmount,
  decayDuration,
  elapsedSeconds,
  fillPrice,
  height = 160,
  title,
  compact = false,
  outputSymbol,
}: DecayCurveProps) {
  // Generate data points along the decay curve
  const numPoints = compact ? 21 : 51;
  const points = Array.from({ length: numPoints }, (_, i) => {
    const t = (i / (numPoints - 1)) * decayDuration;
    const progress = Math.min(t / decayDuration, 1);
    const price = startAmount + (endAmount - startAmount) * progress;
    return {
      time: Math.round(t),
      price: Number(price.toFixed(6)),
    };
  });

  // Dynamic Y-axis range
  const isFlat = startAmount === endAmount;
  let minPrice: number;
  let maxPrice: number;
  if (isFlat) {
    const pad = startAmount * 0.02 || 0.01;
    minPrice = startAmount - pad;
    maxPrice = startAmount + pad;
  } else {
    const lo = Math.min(startAmount, endAmount);
    const hi = Math.max(startAmount, endAmount);
    const pad = (hi - lo) * 0.1;
    minPrice = lo - pad;
    maxPrice = hi + pad;
  }

  const formatTime = (v: number) => {
    if (v >= 3600) return `${(v / 3600).toFixed(1)}h`;
    if (v >= 60) return `${String(Math.round(v / 60))}m`;
    return `${String(v)}s`;
  };

  // Compute explicit X-axis ticks to avoid duplicates
  const computeXTicks = () => {
    let step: number;
    if (decayDuration <= 60) step = 15;
    else if (decayDuration <= 300) step = 60;
    else if (decayDuration <= 3600) step = 300;
    else step = 900;

    const ticks: number[] = [0];
    let t = step;
    while (t < decayDuration) {
      ticks.push(t);
      t += step;
    }
    if (ticks[ticks.length - 1] !== decayDuration) {
      ticks.push(decayDuration);
    }
    return ticks;
  };

  const xTicks = computeXTicks();

  // Gradient colors: green (best) to orange (worst)
  const startColor = "#34d16a";
  const endColor = "#C9A84C";

  return (
    <div>
      {title && (
        <div className="text-text-muted mb-1 text-[10px] uppercase tracking-wider">
          {title}
        </div>
      )}
      {/* Equal start/end note */}
      {isFlat && !compact && (
        <div className="text-text-muted mb-2 text-[10px] italic">
          Start and end amounts are equal — no decay will occur. The filler
          receives a fixed price.
        </div>
      )}
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart
            data={points}
            margin={{
              top: 5,
              right: compact ? 5 : 10,
              left: compact ? -10 : 0,
              bottom: compact ? 0 : 5,
            }}
          >
            <defs>
              <linearGradient id="decayGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={startColor} />
                <stop offset="100%" stopColor={endColor} />
              </linearGradient>
              <linearGradient
                id="decayFillGradient"
                x1="0"
                y1="0"
                x2="1"
                y2="0"
              >
                <stop offset="0%" stopColor={startColor} stopOpacity={0.4} />
                <stop offset="50%" stopColor={startColor} stopOpacity={0.15} />
                <stop offset="100%" stopColor={endColor} stopOpacity={0.03} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              opacity={0.1}
              stroke="rgba(255,255,255,0.1)"
            />
            <XAxis
              dataKey="time"
              fontSize={compact ? 9 : 10}
              tickFormatter={formatTime}
              stroke="rgba(255,255,255,0.3)"
              tickLine={false}
              axisLine={false}
              ticks={xTicks}
              label={
                compact
                  ? undefined
                  : {
                      value: "Time",
                      position: "insideBottomRight",
                      offset: -5,
                      style: {
                        fontSize: 9,
                        fill: "rgba(255,255,255,0.4)",
                      },
                    }
              }
            />
            <YAxis
              domain={[minPrice, maxPrice]}
              fontSize={compact ? 9 : 10}
              tickFormatter={(v: number) => v.toFixed(2)}
              width={compact ? 40 : 50}
              stroke="rgba(255,255,255,0.3)"
              tickLine={false}
              axisLine={false}
              label={
                compact
                  ? undefined
                  : {
                      value: outputSymbol
                        ? `Output (${outputSymbol})`
                        : "Output Amount",
                      angle: -90,
                      position: "insideLeft",
                      offset: 10,
                      style: {
                        fontSize: 9,
                        fill: "rgba(255,255,255,0.4)",
                      },
                    }
              }
            />
            {!compact && (
              <Tooltip
                contentStyle={{
                  backgroundColor: "rgba(22, 26, 32, 0.95)",
                  border: "1px solid rgba(201, 168, 76, 0.2)",
                  borderRadius: 2,
                  fontSize: 11,
                  color: "#F0F0F5",
                }}
                formatter={
                  ((v: number) => [
                    v.toFixed(6),
                    outputSymbol ?? "Output",
                  ]) as never
                }
                labelFormatter={
                  ((v: number) => `${formatTime(v)} elapsed`) as never
                }
              />
            )}
            <Area
              type="linear"
              dataKey="price"
              stroke="url(#decayGradient)"
              strokeWidth={compact ? 2.5 : 3}
              fill="url(#decayFillGradient)"
              dot={false}
              activeDot={
                compact
                  ? false
                  : {
                      r: 3,
                      fill: startColor,
                      stroke: "#fff",
                      strokeWidth: 1,
                    }
              }
            />
            {elapsedSeconds !== undefined &&
              elapsedSeconds <= decayDuration && (
                <ReferenceLine
                  x={Math.min(elapsedSeconds, decayDuration)}
                  stroke="#ef4444"
                  strokeDasharray="4 4"
                  strokeWidth={2}
                  label={{
                    value: `Now (${formatTime(Math.round(elapsedSeconds))})`,
                    fill: "#ef4444",
                    fontSize: compact ? 9 : 10,
                    fontWeight: 600,
                  }}
                />
              )}
            {fillPrice !== undefined && (
              <ReferenceLine
                y={fillPrice}
                stroke="#34d16a"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={{
                  value: "Filled",
                  fill: "#34d16a",
                  fontSize: compact ? 9 : 10,
                  fontWeight: 600,
                }}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
